import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

class IuropLiveSearch extends HTMLElement {
  #events;
  #request;
  #timer;
  #version = 0;
  #cache = new Map();
  #initial;

  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.clear = this.querySelector('[data-search-clear]');
    this.footer = this.querySelector('[data-search-footer]');
    this.dialog = this.closest('dialog-component');
    this.#initial = this.results.innerHTML;
    this.querySelector('form').addEventListener('submit', event => {
      if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
    }, { signal });
    this.input.addEventListener('input', event => this.#schedule(event.isComposing), { signal });
    this.input.addEventListener('compositionend', () => this.#schedule(), { signal });
    this.clear.addEventListener('click', () => {
      this.input.value = '';
      this.#schedule();
      this.input.focus();
    }, { signal });
    this.results.addEventListener('click', event => {
      const term = event.target.closest('[data-search-term]');
      if (!term || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.input.value = term.dataset.searchTerm;
      this.#schedule();
      this.input.focus();
    }, { signal });
    this.addEventListener('keydown', event => this.#navigate(event), { signal });
    this.dialog.addEventListener(DialogCloseEvent.eventName, () => this.#cancel(), { signal });
    this.dialog.addEventListener(DialogOpenEvent.eventName, () => {
      this.input.focus();
      if (this.input.value.trim()) this.#schedule();
    }, { signal });
    document.addEventListener('keydown', event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        this.dialog.toggleDialog();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#cancel();
  }

  #cancel() {
    clearTimeout(this.#timer);
    this.#request?.abort();
    this.#version++;
    this.results?.removeAttribute('aria-busy');
    if (this.status) {
      this.status.textContent = '';
      this.status.removeAttribute('data-error');
    }
  }

  #schedule(composing = false) {
    this.#cancel();
    const term = this.input.value.trim();
    this.clear.hidden = !term;
    this.footer.hidden = !term;
    if (!term) {
      this.results.innerHTML = this.#initial;
      return;
    }
    if (composing) return;
    const version = this.#version;
    this.results.setAttribute('aria-busy', 'true');
    this.status.textContent = this.dataset.loading;
    this.#timer = setTimeout(() => this.#search(term, version), 180);
  }

  async #search(term, version) {
    try {
      let markup = this.#cache.get(term);
      if (!markup) {
        this.#request = new AbortController();
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', 'iurop-live-search');
        url.searchParams.set('resources[type]', 'product,collection,query');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[limit_scope]', 'each');
        const response = await fetch(url, { signal: this.#request.signal });
        if (!response.ok) throw new Error(`Search ${response.status}`);
        const html = await response.text();
        const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-live-results-fragment]');
        if (!fragment) throw new Error('Search fragment missing');
        markup = fragment.outerHTML;
        if (version !== this.#version) return;
        if (this.#cache.size >= 20) this.#cache.delete(this.#cache.keys().next().value);
        this.#cache.set(term, markup);
      }
      if (version !== this.#version) return;
      this.results.innerHTML = markup;
      this.status.textContent = this.results.firstElementChild?.dataset.announcement || '';
      this.results.scrollTop = 0;
    } catch (error) {
      if (version !== this.#version || error.name === 'AbortError') return;
      // Keep useful category links and featured products available on request failure.
      this.results.innerHTML = this.#initial;
      this.status.textContent = this.dataset.error;
      this.status.setAttribute('data-error', '');
    } finally {
      if (version === this.#version) this.results.removeAttribute('aria-busy');
    }
  }

  #navigate(event) {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || event.isComposing) return;
    const links = [...this.results.querySelectorAll('a[href]')];
    if (!links.length) return;
    const current = links.indexOf(document.activeElement);
    if (document.activeElement !== this.input && current < 0) return;
    event.preventDefault();
    if (event.key === 'ArrowUp' && current === 0) return this.input.focus();
    const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : links.length - 1)
      : (current + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
    links[next].focus();
  }
}
if (!customElements.get('iurop-live-search')) customElements.define('iurop-live-search', IuropLiveSearch);
